/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { AuthProps } from "aws-amplify-react/props";
import {
  UpdateUserMutation,
  UserInput,
  saveContactData,
} from "../../../graphql/user";
import {
  Segment,
  Transition,
  Container,
  Dimmer,
  Loader,
} from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import ContactForm from "./ContactForm";
import { ChangePasswordDialog } from ".";
import { authCheckCallApi } from "../../Authenticator/utils";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import ConfirmationDialog from "../../ConfirmationDialog";
import { useTranslation } from "react-i18next";

const Profile: React.FC<AuthProps> = ({ authData }) => {
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [contactFormLoading, setContactFormLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const { t } = useTranslation("common");

  const [updateUser, { loading: updateUserLoading }] = useMutation(
    UpdateUserMutation,
    {
      onError: () => {
        !message &&
          setMessage({
            title: t("update_profile"),
            message: t("update_profile_error"),
          });
      },
    }
  );

  const onSaveContact = async (data: saveContactData) => {
    const userInput: UserInput = {
      id: authData.attributes["custom:uid"],
      name: data.name,
      address: {
        line1: data.addressLine1,
        line2: data.addressLine2,
        city: data.addressCity,
        country: data.addressCountry,
        zip: data.addressZip,
        state: data.addressState,
        telephone1: data.addressTelephone1,
        telephone2: data.addressTelephone2,
      },
      organization: {
        jobTitle: data.jobTitle,
      },
    };

    authCheckCallApi(updateUser, { variables: { user: { ...userInput } } });
  };

  const dimmerActive =
    updateUserLoading || contactFormLoading || message !== null;

  return (
    <React.Fragment>
      <Container id={styles.highlightsContainer}>
        <Transition transitionOnMount animation="fade in" duration="250">
          <Segment className={styles.myAccountSegment}>
            <Dimmer active={dimmerActive}>
              <Loader
                active={dimmerActive}
                size="large"
                disabled={message !== null}
              >
                {(contactFormLoading && t("loading")) ||
                  (updateUserLoading && t("saving"))}
              </Loader>
            </Dimmer>
            <ContactForm
              onFormSubmit={onSaveContact}
              setShowSetPassword={setShowSetPassword}
              userId={authData.attributes["custom:uid"]}
              currentUserRole={
                authData.signInUserSession.idToken.payload.role || ""
              }
              setContactFormLoading={setContactFormLoading}
              readOnly={
                authData.signInUserSession.idToken.payload.plan === "expired"
              }
            />
          </Segment>
        </Transition>
      </Container>
      {showSetPassword && (
        <ChangePasswordDialog setShowSetPassword={setShowSetPassword} />
      )}
      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </React.Fragment>
  );
};

export default Profile;
